import React, { useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

import loadable from '@loadable/component';

import { getFloodAlertAreaById } from '../store/reducers/fmAPI.js';

import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Button from 'react-bootstrap/Button';
import { ListGroup } from 'react-bootstrap';

import MapViewHeaderComponent from '../components/MapViewHeaderComponent.js'
import FloodAlertSummaryComponent from '../components/FloodAlertSummaryComponent.js';
import Loading from '../components/Loading.js';
import NearByStationsComponent from '../components/NearByStationsComponent.js';
import WaterBodySummaryComponent from '../components/WaterBodySummaryComponent.js';
import AdComponent from '../components/AdComponent.js';

import CommonHeaders from '../components/helmet-headers/CommonHeaders.js';
import Breadcrumbs, { CRUMBS } from '../components/helmet-headers/Breadcrumbs.js';

import { formatTimestamp } from '../lib/formatHelpers.js';

const FMCoverageAreas = loadable(() => import('../components/FMCoverageAreas.js'));
const GoverningBodyName = loadable(() => import('../components/GoverningBodyName.js'));


// XPA Components
const AppPushEnableIcon = loadable(() => import('../components/AppPushEnableIcon.js'), { ssr: false, fallback: null });
const AppManageFAAModal = loadable(() => import('../components/AppManageFAAModal.js'), { ssr: false, fallback: null });
const AppFAASubscribe = loadable(() => import('../components/AppFAASubscribe.js'), { ssr: false, fallback: null });

function FloodAlertView() {
	const params = useParams();
	const dispatch = useDispatch();
	const { floodAlerts } = useSelector(state => state.fmAPI);
	const { appFocusCount, userId, appUserHome } = useSelector(state => state.cdvState);


	const localFloodNavsRef = useRef(null);

	const scrollToLocalFloodNavSection = () => {
		localFloodNavsRef.current?.scrollIntoView({ behavior: 'smooth' });
	};


	useEffect(() => {
		dispatch(getFloodAlertAreaById(params.floodAlertAreaId));
	}, [dispatch, params.floodAlertAreaId, appFocusCount]);


	if(floodAlerts[params.floodAlertAreaId]) {
		let floodAlert = floodAlerts[params.floodAlertAreaId];

		let appManagementPane = null;
		if(userId && appUserHome && floodAlert && appUserHome.faa) {
			console.log("faa", JSON.stringify(appUserHome.faa))
			console.log("api", JSON.stringify(floodAlert));
			const matchingRecord = appUserHome.faa.find(item => item.floodAlertAreaId === floodAlert.floodAlertAreaId);
			if(matchingRecord) {
				appManagementPane = <Row style={{marginBottom: "7px"}}>
					<Col xs={8}>
						<AppManageFAAModal
							floodAlertAreaName={matchingRecord.floodAlertAreaName}
							floodAlertAreaId={matchingRecord.floodAlertAreaId}
							pushEnableBySetting={matchingRecord.pushEnable}
						/>
					</Col>
					<Col xs={4}>
						<span style={{fontSize: "1.6em"}}>
							<AppPushEnableIcon pushEnable={matchingRecord.pushEnable} />
						</span>
					</Col>
				</Row>;
			} else {
				appManagementPane = <Row style={{marginBottom: "7px"}}>
					<Col xs={12}><AppFAASubscribe floodAlertAreaId={floodAlert.floodAlertAreaId} /></Col>
				</Row>;
			}
		}

		let hasFloodNavs = floodAlert.nearByFloodNavs && (floodAlert.nearByFloodNavs.length > 0);

		let imageUrl = "https://static.floodmapper.co.uk/static/cdn/flood-clear-icon-960-h140.webp"
		if(floodAlert.activeAlert) {
			switch(floodAlert.activeAlert.severityLevel) {
				case 1: /* Severe */
					imageUrl="https://static.floodmapper.co.uk/static/cdn/flood-severe-warning-icon-960-h140.webp";
					break;
				case 2: /* Warning */
					imageUrl="https://static.floodmapper.co.uk/static/cdn/flood-warning-icon-960-h140.webp";
					break;
				case 3: /* Alert */
					imageUrl="https://static.floodmapper.co.uk/static/cdn/flood-alert-icon-960-h140.webp";
					break;
			}
		}

		return <div>
			<CommonHeaders
				title={(floodAlert.floodAlertAreaName && floodAlert.floodAlertAreaName + " - Flood Alert and Warning Area") || "Unknown Flood Alert Area"}
				description={
				  floodAlert.activeAlert && floodAlert.activeAlert.liveMessage
				    ? floodAlert.activeAlert.liveMessage.length > 140 
				      ? floodAlert.activeAlert.liveMessage.trim().substring(0, 137) + "... Read More"
				      : floodAlert.activeAlert.liveMessage
				    : "No active alert for " + floodAlert.floodAlertAreaName
				}
				canonicalDefaultWebsite="UK"
				canonicalUrlPath={"/flood-alerts/" + floodAlert.fwisCode}
				ogType={"place"}
				lat={floodAlert.lat}
				lng={floodAlert.lng}
				imageUrl={imageUrl}
			/>
			<Breadcrumbs
	            builtinCrumbs={[CRUMBS.HOME, CRUMBS.DATA_EXPLORER]}
	            leafCrumbs={[{
	                name: (floodAlert.floodAlertAreaName && floodAlert.floodAlertAreaName + " - Flood Alert and Warning Area") || "Unknown Flood Alert Area",
	                path: "/flood-alerts/" + floodAlert.fwisCode
	            }]}
	        />

			<Row className="screen-bottom-border">
				<Col xs={12} lg={1} xl={2} className="d-none d-lg-block large-screen-left">&nbsp;</Col>
				<Col xs={12} lg={10} xl={8}>
					<MapViewHeaderComponent
						lat={floodAlert.lat}
						lng={floodAlert.lng}
						label={floodAlert.floodAlertAreaName || "Unknown Flood Alert Area"}
						geoJSON={floodAlert.polygon}
					/>
					{appManagementPane}
					{hasFloodNavs && <Row>
						<Col xs={12} className="bg-secondary text-white">
							<b><i>This area has {floodAlert.nearByFloodNavs.length === 1 ? "a " : floodAlert.nearByFloodNavs.length+" "}live FloodNav{floodAlert.nearByFloodNavs.length > 1 ? "s" : ""}</i></b>{"   "}<Button size="sm" variant="dark" onClick={function() {
								scrollToLocalFloodNavSection();
							}}>View</Button>
						</Col>
					</Row>}
					<div>
						{floodAlert.activeAlert && <Row>
							<Col xs={12}>
								<FloodAlertSummaryComponent enableFloodWarningAdvice={true} className={"hX-scale11em"} button={false} severityLevel={floodAlert.activeAlert.severityLevel} areaName={floodAlert.description || floodAlert.activeAlert.liveDescription} fwisCode={floodAlert.fwisCode} />
							</Col>
						</Row>}
						{floodAlert.activeAlert && <Row>
							<Col xs={12} style={{textAlign: "justify", paddingLeft: "20px", paddingRight: "20px"}}>
								{floodAlert.activeAlert.liveMessage}
							</Col>
							<Col xs={12}>
								<i>Text as issued by <b><GoverningBodyName governingBody={floodAlert.governingBody} /></b></i>
							</Col>
							<Col xs={12}>
								<i><b>Last updated at:</b> {formatTimestamp(floodAlert.activeAlert.timeMessageChanged)}</i>
							</Col>
						</Row>}
						{(process.env.REACT_APP_ENABLE_GOOGLE_ADS === "true" && process.env.REACT_APP_ENABLE_GOOGLE_ADS_ON_FLOODALERTAREAS === "true") && <Row style={{marginTop: "17px"}}>
							<Col xs={12}>
								<AdComponent
								  client="ca-pub-6790224688210339"
								  slot="6130981811"
								  format="fluid"
								  layoutKey="-fb+5w+4e-db+86"
								  responsive={true}
								/>
							</Col>
						</Row>}
						{!floodAlert.activeAlert && <Row>
							<FloodAlertSummaryComponent button={false} severityLevel={4} areaName={floodAlert.floodAlertAreaName} fwisCode="" />
						</Row>}
						{!floodAlert.activeAlert && <Row>
							<Col xs={12}><i>This area is not currently subject to a flood alert or warning</i></Col>
						</Row>}
					</div>
					{floodAlert.waterBody && <Row  style={{paddingBottom: "7px", display: 'flex', alignItems: 'center'}}>
						<Col xs={12} style={{display: 'flex', flexDirection: 'column', justifyContent: 'center'}}>
							<WaterBodySummaryComponent
										waterBodyName={floodAlert.waterBody.waterBodyName}
										geoJSON={floodAlert.waterBody.polygon}
										floodAreas={floodAlert.waterBody.floodAlertAreasLive}
										waterBodyId={floodAlert.waterBodyId}
									/>
						</Col>
					</Row>}
					{floodAlert.nearByFloodNavs && floodAlert.nearByFloodNavs.length > 0 && <div ref={localFloodNavsRef}>
						<Row>
							<Col xs={12} className="text-white bg-dark"><b>Local FloodNavs</b></Col>
							<Col xs={12}>
								<i>There are {floodAlert.nearByFloodNavs.length} local FloodNav{floodAlert.nearByFloodNavs.length > 1 && "s"} for this flood alert area</i>
							</Col>
							<Col xs={12}>
								<FMCoverageAreas fmCoverageAreas={floodAlert.nearByFloodNavs} enableHeader={false} />
							</Col>
						</Row>
					</div>}
					{((floodAlert.nearByStations && floodAlert.nearByStations.length > 0) || floodAlert.floodAlerts || floodAlert.floodAlertAreas) && <Row>
						<Col xs={12} className="bg-dark text-white"><b>Further Local Information</b></Col>
					</Row>}
					<div>
						<Row>
							{floodAlert.floodAlerts && <Col xs={12} sm={6} md={4} xlg={4}>
								<ListGroup>
									<ListGroup.Item variant="dark" className="bg-dark text-white">
									Nearby Flood Warnings
									</ListGroup.Item>
									{floodAlert.floodAlerts && floodAlert.floodAlerts.map(function(fa) {
										return <ListGroup.Item>
											<FloodAlertSummaryComponent key={fa.fwisCode} severityLevel={fa.severityLevel} areaName={fa.liveDescription} fwisCode={fa.fwisCode} />
										</ListGroup.Item>
									})}
									{(!floodAlert.floodAlerts || floodAlert.floodAlerts.length === 0) && <ListGroup.Item><i><b>None</b></i></ListGroup.Item>}
								</ListGroup>
							</Col>}
							{floodAlert.floodAlertAreas && <Col xs={12} sm={6} md={4} xlg={4}>
								<ListGroup>
									<ListGroup.Item variant="dark" className="bg-dark text-white">
									Nearby Alert Areas
									</ListGroup.Item>
									<div>
										{floodAlert.floodAlertAreas && floodAlert.floodAlertAreas.map(function(fa) {
											return <ListGroup.Item>
												<FloodAlertSummaryComponent key={fa.fwisCode} severityLevel={"4"} areaName={fa.floodAlertAreaName} fwisCode={fa.fwisCode} />
											</ListGroup.Item>
										})}
										{(!floodAlert.floodAlertAreas || floodAlert.floodAlertAreas.length === 0) && <ListGroup.Item><i><b>None</b></i></ListGroup.Item>}
									</div>
								</ListGroup>
							</Col>}
							{floodAlert.nearByStations && floodAlert.nearByStations.length > 0 && 
									<Col xs={12} sm={4} md={4} xlg={4}>
									<NearByStationsComponent headingText="Local Stations" stations={floodAlert.nearByStations} />
								</Col>}
						</Row>
					</div>
				</Col>
				<Col xs={12} lg={1} xl={2} className="d-none d-lg-block large-screen-right">&nbsp;</Col>
				</Row>
			</div>;
	} else {
		return <Loading />;
	}
}

export default FloodAlertView;